import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useRoom } from "./RoomProvider";
import { useCallSession } from "../../hooks/useCallSession";
import { apiClient } from "../../lib/api";
import { autoPrefixUS } from "../../components/PhoneInput";

/**
 * CallBreakoutRoom — the "room within the room."
 *
 * When the VA starts a call from anywhere on the Floor (trip focus card,
 * incoming offer, coworker whisper), this modal takes over the workspace —
 * NOT a browser popup, NOT another tab. It's part of the Floor.
 *
 * Layout:
 *   ┌───────────────────────────────────────────────────────┐
 *   │  BREAKOUT · <purpose>              ⏱ 02:14   ● REC    │
 *   │  ┌─────────────────┐   ┌───────────────────────────┐  │
 *   │  │ Participant tiles│   │ CRM Wrap Panel            │  │
 *   │  │  · VA (mod)      │   │  Name / phone / stage     │  │
 *   │  │  · driver        │   │  Recent activity          │  │
 *   │  │  · rider         │   │  Trip context (if any)    │  │
 *   │  │  · Nova (AI)     │   │                           │  │
 *   │  └─────────────────┘   └───────────────────────────┘  │
 *   │  [ transfer to AI ] [ invite ] [ video ] [ hangup ]   │
 *   └───────────────────────────────────────────────────────┘
 *
 * Video is a P2 wire-in (SignalWire Video SDK); the button lives here now
 * so the affordance is present, but it flips a flag on the call session
 * rather than starting an actual video stream.
 */
export default function CallBreakoutRoom() {
  const { activeCall, transferToAi, hangup, invite, crmLookup } =
    useContextCall();
  const [elapsed, setElapsed] = useState(0);
  const [crmCards, setCrmCards] = useState({}); // phone → contact
  const [inviteOpen, setInviteOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);

  // Elapsed timer
  useEffect(() => {
    if (!activeCall?.created_at) return undefined;
    const start = new Date(activeCall.created_at).getTime();
    const iv = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [activeCall?.created_at]);

  // Auto-lookup CRM for each participant on join
  useEffect(() => {
    if (!activeCall?.participants) return;
    activeCall.participants.forEach(async (p) => {
      if (!p.phone || crmCards[p.phone] !== undefined) return;
      const c = await crmLookup(p.phone);
      setCrmCards((prev) => ({ ...prev, [p.phone]: c || null }));
    });
  }, [activeCall, crmLookup, crmCards]);

  // A new/different call replaces whatever was minimized before it.
  useEffect(() => { setMinimized(false); }, [activeCall?.id]);

  if (!activeCall) return null;

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const humanCount = (activeCall.participants || []).filter(p => p.kind !== "ai").length;

  // Minimized: a small corner pill — the call keeps running, but every other
  // Floor surface (compose forms, dialer, lanes) stays fully reachable. Fixes
  // a real hazard: a single inbound call used to lock the entire workspace.
  if (minimized) {
    return (
      <button
        type="button"
        className="breakout-pill"
        data-testid="floor-breakout-room-minimized"
        onClick={() => setMinimized(false)}
      >
        <span className="bo-pulse" />
        <span>On call · {mmss}</span>
        <span className="breakout-pill-count">{humanCount} on the line</span>
      </button>
    );
  }

  return (
    <div className={`breakout ${activeCall.priority === "emergency" ? "breakout--emergency" : ""}`}
         role="dialog" data-testid="floor-breakout-room">
      <div className="breakout-card">
        <header className="bo-head">
          <div className="bo-head-left">
            <span className="bo-pulse" />
            <span className="bo-label">
              BREAKOUT · {(activeCall.purpose || "va_bridge").replace(/_/g, " ")}
            </span>
            <span className="bo-count">{humanCount} on the line</span>
            {activeCall.priority === "emergency" && (
              <span className="bo-emergency-badge" data-testid="breakout-emergency-badge">🔴 EMERGENCY</span>
            )}
          </div>
          <div className="bo-head-right">
            <span className="bo-timer" data-testid="breakout-timer">⏱ {mmss}</span>
            <span className="bo-rec">● recording</span>
            <button
              type="button"
              className="bo-minimize-btn"
              data-testid="floor-breakout-room-minimize-btn"
              onClick={() => setMinimized(true)}
              title="Minimize — keep the call going, get back to the Floor"
            >
              minimize
            </button>
          </div>
        </header>

        <div className="bo-body">
          <div className="bo-tiles">
            {(activeCall.participants || []).map((p, i) => (
              <ParticipantTile key={i} p={p} crm={crmCards[p.phone]} />
            ))}
          </div>
          <div className="bo-wrap">
            {activeCall.video_active && activeCall.jitsi_room ? (
              <JitsiVideoPanel room={activeCall.jitsi_room}
                               displayName={activeCall.initiator?.email || "MedexOVA VA"} />
            ) : (
              <WrapPanel activeCall={activeCall} crmCards={crmCards} />
            )}
          </div>
        </div>

        <footer className="bo-actions">
          <button
            className="bo-btn"
            onClick={() => setInviteOpen(v => !v)}
            data-testid="breakout-invite"
          >invite</button>
          <button
            className="bo-btn"
            onClick={() => transferToAi()}
            data-testid="breakout-to-ai"
            disabled={activeCall.ai_assist}
          >{activeCall.ai_assist ? "AI on line" : "hand mic to Nova"}</button>
          <button
            className="bo-btn bo-btn--ghost"
            data-testid="breakout-video"
            title="Turn on video · Jitsi Meet inside the Floor"
            onClick={async () => {
              if (!activeCall) return;
              if (activeCall.video_active) {
                await apiClient.post(`/floor/calls/${activeCall.id}/video/disable`);
              } else {
                await apiClient.post(`/floor/calls/${activeCall.id}/video/enable`);
              }
              // useCallSession poller re-hydrates via /active
            }}
          >
            {activeCall?.video_active ? "video · on" : "start video"}
          </button>
          <button
            className="bo-btn bo-btn--danger"
            onClick={() => hangup()}
            data-testid="breakout-hangup"
          >hangup</button>
        </footer>

        {inviteOpen && <InviteForm onInvite={async (t) => {
          const res = await invite(t);
          if (res?.ok === false) toast.error(res?.error || "Couldn't invite that number");
          setInviteOpen(false);
        }} onCancel={() => setInviteOpen(false)} />}
      </div>
    </div>
  );
}

function ParticipantTile({ p, crm }) {
  const initials = String(p.name || p.phone || "?").split(/\s+/)
    .map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const statusColor = p.status === "joined" ? "#7fd8b7"
    : p.status === "dialing" ? "#f59e0b"
    : p.status === "failed" ? "#ef4444"
    : "#94a3b8";
  const isAI = p.kind === "ai";
  return (
    <div className={`bo-tile ${p.moderator ? "bo-tile--mod" : ""} ${isAI ? "bo-tile--ai" : ""}`}
         data-testid={`breakout-participant-${p.kind}-${p.id || p.phone}`}>
      <div className="bo-tile-avatar">
        <span>{isAI ? "☆" : initials}</span>
        <span className="bo-tile-dot" style={{ background: statusColor }} />
      </div>
      <div className="bo-tile-body">
        <div className="bo-tile-name">
          {p.name || (isAI ? "Nova" : "unknown")}
          {p.moderator && <span className="bo-tile-mod">MOD</span>}
        </div>
        <div className="bo-tile-meta">
          {p.kind}{p.phone ? ` · ${p.phone.slice(-10)}` : ""}
        </div>
        <div className="bo-tile-status">
          {p.status === "dialing" ? "ringing…"
            : p.status === "joined" ? "on call"
            : p.status === "failed" ? "no answer"
            : p.status}
        </div>
        {crm && <div className="bo-tile-crm">CRM · {crm.stage || crm.tags?.[0] || "known"}</div>}
      </div>
    </div>
  );
}

function WrapPanel({ activeCall, crmCards }) {
  const primary = (activeCall.participants || []).find(p =>
    p.kind !== "va" && p.kind !== "ai" && p.status !== "failed",
  ) || (activeCall.participants || [])[1];
  const contact = primary?.phone ? crmCards[primary.phone] : null;
  const handoff = activeCall.ai_handoff;

  return (
    <div className="bo-wrap-panel">
      {handoff && (
        <div className="bo-handoff" data-testid="breakout-ai-handoff">
          <div className="bo-wrap-title">AI handoff · {activeCall.intent?.replace(/_/g, " ")}</div>
          {handoff.caller_said && <div className="bo-handoff-quote">"{handoff.caller_said}"</div>}
          <div className="bo-wrap-row"><span>summary</span>{handoff.summary}</div>
          {handoff.emergency_type && (
            <div className="bo-wrap-row"><span>type</span>{handoff.emergency_type}</div>
          )}
        </div>
      )}
      <div className="bo-wrap-title">wrap · CRM</div>
      {!primary && <div className="bo-wrap-empty">no other party yet</div>}
      {primary && (
        <>
          <div className="bo-wrap-name">{primary.name || "unknown"}</div>
          <div className="bo-wrap-role">{primary.kind}</div>
          {primary.phone && <div className="bo-wrap-phone">{primary.phone}</div>}
          {contact ? (
            <>
              {contact.email && <div className="bo-wrap-row"><span>email</span>{contact.email}</div>}
              {contact.stage && <div className="bo-wrap-row"><span>stage</span>{contact.stage}</div>}
              {Array.isArray(contact.tags) && contact.tags.length > 0 && (
                <div className="bo-wrap-row"><span>tags</span>{contact.tags.join(" · ")}</div>
              )}
              {contact.notes && <div className="bo-wrap-notes">{contact.notes}</div>}
            </>
          ) : (
            <div className="bo-wrap-empty">no CRM record · <em>add to contacts?</em></div>
          )}
          {activeCall.trip_id && (
            <div className="bo-wrap-trip">trip · {String(activeCall.trip_id).slice(0, 8)}</div>
          )}
        </>
      )}
    </div>
  );
}

function InviteForm({ onInvite, onCancel }) {
  const [kind, setKind] = useState("driver");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  return (
    <div className="bo-invite" data-testid="breakout-invite-form">
      <div className="bo-invite-row">
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="breakout-invite-kind">
          <option value="driver">driver</option>
          <option value="rider">rider</option>
          <option value="tp">TP</option>
          <option value="broker">broker</option>
          <option value="external">external</option>
        </select>
        <input placeholder="+1 555 555 0100" value={phone} onChange={(e) => setPhone(e.target.value)}
               onBlur={() => setPhone((p) => autoPrefixUS(p))}
               data-testid="breakout-invite-phone" />
        <input placeholder="name (optional)" value={name} onChange={(e) => setName(e.target.value)}
               data-testid="breakout-invite-name" />
      </div>
      <div className="bo-invite-actions">
        <button className="bo-btn bo-btn--ghost" onClick={onCancel}>cancel</button>
        <button className="bo-btn" onClick={() => onInvite({ kind, phone, name })} disabled={!phone}>
          ring
        </button>
      </div>
    </div>
  );
}

// ─── Consume the call session via a context slot on the RoomProvider ───
// (Provided by Floor.jsx wrapper so we don't create two polling loops.)
function useContextCall() {
  const { callSession } = useRoom();
  if (callSession) return callSession;
  // Fallback: create a local session (safe — shares the same server state).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useCallSession();
}

// ─── Jitsi Meet External API — real video panel inside the Floor ───
// Loads the meet.jit.si Jitsi Meet External API script once, then mounts a
// full JitsiMeetJS instance inside the breakout modal. Full camera / mic /
// screen-share / participant list, all governed by the same room name the
// backend chose so participants who tapped their SMS join link land in the
// same room. No external accounts, no keys.
function JitsiVideoPanel({ room, displayName }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  useEffect(() => {
    if (!room) return undefined;
    let cancelled = false;

    const mount = () => {
      if (cancelled) return;
      if (!containerRef.current) return;
      // eslint-disable-next-line no-undef
      if (!window.JitsiMeetExternalAPI) return;
      // Tear down any previous instance
      if (apiRef.current) { try { apiRef.current.dispose(); } catch { /* noop */ } }
      // eslint-disable-next-line no-undef
      apiRef.current = new window.JitsiMeetExternalAPI("meet.jit.si", {
        roomName: room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: "#0a1420",
          TOOLBAR_BUTTONS: [
            "microphone", "camera", "desktop", "fullscreen", "hangup",
            "chat", "raisehand", "tileview", "settings",
          ],
        },
      });
    };

    if (window.JitsiMeetExternalAPI) {
      mount();
    } else if (document.getElementById("jitsi-external-api-script")) {
      document.getElementById("jitsi-external-api-script").addEventListener("load", mount, { once: true });
    } else {
      const s = document.createElement("script");
      s.id = "jitsi-external-api-script";
      s.src = "https://meet.jit.si/external_api.js";
      s.async = true;
      s.onload = mount;
      document.head.appendChild(s);
    }

    return () => {
      cancelled = true;
      if (apiRef.current) { try { apiRef.current.dispose(); } catch { /* noop */ } apiRef.current = null; }
    };
  }, [room, displayName]);

  return (
    <div className="bo-jitsi" data-testid="breakout-jitsi">
      <div ref={containerRef} className="bo-jitsi-frame" />
      <div className="bo-jitsi-foot">
        room: <code>{room}</code>
      </div>
    </div>
  );
}

